class RateLimiter {
  constructor() {
    this.requestQueue = [];
    this.processing = false;
    this.requestCounts = {
      perSecond: 0,
      perMinute: 0
    };
    this.lastSecondReset = Date.now();
    this.lastMinuteReset = Date.now();
    
    // Nibiru limits: 6 requests per second, 180 per minute
    this.limits = {
      perSecond: 6,
      perMinute: 180
    };
  }

  async makeRequest(requestFn, priority = 'normal') {
    return new Promise((resolve, reject) => {
      const request = {
        fn: requestFn,
        resolve,
        reject,
        priority,
        timestamp: Date.now()
      };

      // Add to queue based on priority
      if (priority === 'high') {
        this.requestQueue.unshift(request);
      } else {
        this.requestQueue.push(request);
      }

      this.processQueue();
    });
  }

  async processQueue() {
    if (this.processing || this.requestQueue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.requestQueue.length > 0) {
      this.resetCountersIfNeeded();

      if (this.canMakeRequest()) {
        const request = this.requestQueue.shift();
        
        try {
          this.incrementCounters();
          const result = await request.fn();
          request.resolve(result);
        } catch (error) {
          request.reject(error);
        }
      } else {
        // Wait before trying again
        const waitTime = this.calculateWaitTime();
        await this.delay(waitTime);
      }
    }

    this.processing = false;
  }

  resetCountersIfNeeded() {
    const now = Date.now();
    
    if (now - this.lastSecondReset >= 1000) {
      this.requestCounts.perSecond = 0;
      this.lastSecondReset = now;
    }
    
    if (now - this.lastMinuteReset >= 60000) {
      this.requestCounts.perMinute = 0;
      this.lastMinuteReset = now;
    }
  }

  canMakeRequest() {
    return this.requestCounts.perSecond < this.limits.perSecond && 
           this.requestCounts.perMinute < this.limits.perMinute;
  }

  incrementCounters() {
    this.requestCounts.perSecond++;
    this.requestCounts.perMinute++;
  }

  calculateWaitTime() {
    const now = Date.now();
    const secondWait = 1000 - (now - this.lastSecondReset);
    const minuteWait = 60000 - (now - this.lastMinuteReset);
    
    if (this.requestCounts.perSecond >= this.limits.perSecond) {
      return Math.max(secondWait, 100); // Wait at least 100ms
    }
    
    if (this.requestCounts.perMinute >= this.limits.perMinute) {
      return Math.max(minuteWait, 1000); // Wait at least 1s
    }
    
    return 200; // Default small delay
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Get queue status for debugging
  getStatus() {
    return {
      queueLength: this.requestQueue.length,
      requestCounts: { ...this.requestCounts },
      processing: this.processing
    };
  }
}

// Export singleton instance
export const rateLimiter = new RateLimiter();